import { Grid, Menu } from '@mui/joy';
import type { ReactElement } from 'react';
import { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import DialogDropdown from 'components/technical/DialogDropdown/DialogDropdown.tsx';
import { GCardMenuButton } from 'components/technical/GCard/GCardMenuButton.tsx';
import { cardHeight, gridWidth } from './accountTab.utils.ts';
import { useAccountsSuspenseQuery } from 'generated/graphql';
import { venues } from 'components/venue/VenueData.tsx';
import { useFeedback } from 'components/technical/Feedback/UseFeedback.tsx';
import type { IBKRCallbackState } from '../IbkrCallback.tsx';
import ConnectInteractiveBrokersButton from './ConnectInteractiveBrokersButton.tsx';
import InteractiveBrokersConnection from '../createAccount/InteractiveBrokersConnection.tsx';
import { useAuth } from 'components/management/NewAuthProvider';
import AccountCard from '../AccountCard.tsx';

const NonCryptoIntegrationsTab = (): ReactElement => {
  const { data } = useAccountsSuspenseQuery();
  const { isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const feedback = useFeedback();

  const nonCryptoAccounts = data.portfolio.accounts.filter(
    (account) => venues[account.venue.label]?.type === 'non-crypto'
  );

  const oauthState = location.state as IBKRCallbackState | undefined;
  const hasOAuthCallback = oauthState?.action === 'connect';
  const hasValidParams = hasOAuthCallback && oauthState.token && oauthState.verifier;

  useEffect(() => {
    if (hasOAuthCallback && !hasValidParams) {
      feedback.showErrorMessage(
        'Missing required OAuth parameters from Interactive Brokers. Please try connecting again.'
      );
      console.error('Missing OAuth parameters:', {
        token: oauthState?.token,
        verifier: oauthState?.verifier,
      });
    }
  }, [hasOAuthCallback, hasValidParams, feedback, oauthState]);

  const handleOAuthComplete = useCallback(() => {
    navigate(location.pathname, { replace: true });
  }, [navigate, location.pathname]);

  if (hasValidParams) {
    return (
      <InteractiveBrokersConnection
        oauthParams={{ token: oauthState.token!, verifier: oauthState.verifier! }}
        onComplete={handleOAuthComplete}
      />
    );
  }

  return (
    <Grid container spacing={3}>
      {isAdmin && (
        <DialogDropdown>
          <GCardMenuButton height={cardHeight} gridWidth={gridWidth} />
          <Menu
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, -70],
                },
              },
            ]}
          >
            <ConnectInteractiveBrokersButton />
          </Menu>
        </DialogDropdown>
      )}

      {nonCryptoAccounts.map((account) => (
        <AccountCard key={account.id} account={account} gridWidth={gridWidth} height={cardHeight} />
      ))}
    </Grid>
  );
};

export default NonCryptoIntegrationsTab;
